// ────────────────────────────────────────────────────────────────────
// Upload manager — floating bottom-right, per-file progress, Stop and Retry.
// ────────────────────────────────────────────────────────────────────

import { UploadCloud, X, ChevronDown, ChevronUp, RotateCw, CheckCircle2, AlertCircle } from "lucide-react";
import { DOCK_TOP_PX } from "@/components/dock/DockBar";
import { MAX_MB, type UploadItem, type UpStatus } from "@/lib/intakeFiles";


/** The upload panel is `fixed` in the bottom-right corner, and so is the dock —
 *  at z-1200, above this panel's z-1150. Sharing that corner was invisible while
 *  uploads only ever started from a file manager nobody used; now that uploading
 *  is the primary action of this screen, an upload in progress would draw its
 *  rows underneath the dock buttons. Measured on production 2026-08-04: 227 of
 *  the panel's 360px width sat below the dock.
 *
 *  So it stacks above the dock instead of behind it. DOCK_TOP_PX is published by
 *  DockBar for exactly this; the +8 is the gap between the two.
 *  DockBar.test.tsx fails if this drops back into the dock's band. */
export const UPLOAD_PANEL_BOTTOM_PX = DOCK_TOP_PX + 8;

// ── Upload manager (floating bottom-right, per-file progress) ──
export function UploadPanel({ items, open, setOpen, onStop, onRetry, onRetryAll, onCancel, onClose }: {
  items: UploadItem[]; open: boolean; setOpen: (v: boolean) => void;
  onStop: () => void; onRetry: (key: string) => void; onRetryAll: () => void; onCancel: (key: string) => void; onClose: () => void;
}): JSX.Element {
  const active = items.filter((i) => i.status === "uploading" || i.status === "queued").length;
  const done = items.filter((i) => i.status === "done").length;
  const failed = items.filter((i) => i.status === "failed" || i.status === "stopped").length;
  const retryable = items.filter((i) => (i.status === "failed" || i.status === "stopped") && i.size > 0 && i.size <= MAX_MB * 1024 * 1024).length;
  const title = active > 0 ? `Uploading ${done}/${items.length}` : `${done} uploaded${failed ? ` · ${failed} failed` : ""}`;
  return (
    <div className="fixed z-[1150] shadow-2xl" style={{ right: 20, bottom: UPLOAD_PANEL_BOTTOM_PX, width: 360, background: "var(--paper)", border: "1px solid var(--hairline)" }}>
      <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: "var(--hairline)", background: "var(--paper-2)" }}>
        <UploadCloud size={13} style={{ color: "var(--accent)" }} />
        <span className="font-mono flex-1 truncate" style={{ fontSize: 11.5, color: "var(--ink-1)", fontWeight: 600 }}>{title}</span>
        {active > 0 && (
          <button onClick={onStop} className="font-mono uppercase px-1.5 py-0.5 border transition hover:bg-[rgba(192,57,43,0.08)] active:translate-y-px"
                  style={{ fontSize: 8.5, letterSpacing: ".1em", color: "var(--err)", borderColor: "var(--err)", fontWeight: 700 }}>Stop</button>
        )}
        <button onClick={() => setOpen(!open)} title={open ? "Collapse" : "Expand"} style={{ color: "var(--ink-3)" }}>
          {open ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
        </button>
        {active === 0 && <button onClick={onClose} title="Close" style={{ color: "var(--ink-3)" }}><X size={14} /></button>}
      </div>
      {open && (
        <div className="max-h-[300px] overflow-auto">
          {items.map((it) => (
            <div key={it.key} className="px-3 py-2 border-b" style={{ borderColor: "var(--hairline)" }}>
              <div className="flex items-center gap-2">
                <UpIcon status={it.status} />
                <span className="font-mono flex-1 truncate" style={{ fontSize: 11, color: "var(--ink-1)" }}>{it.name}</span>
                <span className="font-mono tabular-nums shrink-0" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                  {it.status === "uploading" ? `${it.pct}%`
                    : it.status === "done" ? "done"
                    : it.status === "queued" ? "queued"
                    : it.note ?? it.status}
                </span>
                {(it.status === "uploading" || it.status === "queued") && (
                  <button onClick={() => onCancel(it.key)} title="Cancel this file" className="shrink-0 hover:opacity-70" style={{ color: "var(--err)" }}><X size={13} /></button>
                )}
                {(it.status === "failed" || it.status === "stopped") && it.size > 0 && it.size <= MAX_MB * 1024 * 1024 && (
                  <button onClick={() => onRetry(it.key)} title="Retry" className="shrink-0 hover:opacity-70" style={{ color: "var(--accent-deep)" }}><RotateCw size={12} /></button>
                )}
              </div>
              {(it.status === "uploading" || it.status === "queued" || it.status === "done") && (
                <div className="mt-1.5" style={{ height: 3, background: "var(--hairline)" }}>
                  <div style={{ height: "100%", width: `${it.status === "done" ? 100 : it.pct}%`,
                                background: it.status === "done" ? "var(--ok)" : "var(--accent)", transition: "width .2s" }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {retryable > 0 && active === 0 && (
        <button onClick={onRetryAll} className="w-full font-mono uppercase py-2 border-t transition hover:bg-[rgba(var(--accent-rgb)_/_0.06)]"
                style={{ borderColor: "var(--hairline)", fontSize: 9.5, letterSpacing: ".1em", color: "var(--accent-deep)", fontWeight: 700 }}>
          Retry {retryable} failed
        </button>
      )}
    </div>
  );
}
export function UpIcon({ status }: { status: UpStatus }): JSX.Element {
  if (status === "done") return <CheckCircle2 size={13} style={{ color: "var(--ok)" }} />;
  if (status === "failed" || status === "stopped") return <AlertCircle size={13} style={{ color: "var(--err)" }} />;
  if (status === "uploading") return <UploadCloud size={13} style={{ color: "var(--accent)" }} />;
  return <UploadCloud size={13} style={{ color: "var(--ink-4)" }} />;
}

